import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSession } from "@/context/SessionContext";
import { errMsg, fmtDateTime, fmtINR } from "@/lib/format";
import { Badge, EmptyState, Field } from "@/components/common/States";

export const TONE = { draft: "neutral", pending_approval: "invited", pending: "invited", approved: "active", rejected: "suspended", changes_requested: "pending", superseded: "inactive", converted: "gold", cancelled: "inactive", DELIVERED: "active", CANCELLED: "inactive", READY_FOR_DELIVERY: "active", QUALITY_CHECK: "invited", IN_PROGRESS: "gold" };
export const St = ({ s, ...rest }) => <Badge tone={TONE[s] || "neutral"} {...rest}>{String(s).replace(/_/g, " ")}</Badge>;
export const money = (v) => fmtINR(Number(v || 0));

export const Panel = ({ title, actions, children, testId }) => (
  <div className="g39-card" data-testid={testId}>
    <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2"><span className="font-display font-semibold">{title}</span><div className="flex flex-wrap gap-2">{actions}</div></div>
    <div className="p-4">{children}</div>
  </div>
);

export const SimpleTable = ({ rows, cols, empty, testId, link }) => (
  !rows?.length ? <EmptyState title={empty} testId={`${testId}-empty`} /> : (
    <div className="overflow-x-auto"><table className="w-full text-sm" data-testid={testId}>
      <thead><tr className="text-left g39-label border-b">{cols.map((c) => <th key={c[0]} className="py-2 px-2">{c[0]}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} className="border-b last:border-0 hover:bg-secondary/40">{cols.map((c, i) => (
          <td key={c[0]} className="py-2 px-2">{i === 0 && link ? <Link className="font-medium hover:text-gold" to={link(r)} data-testid={`${testId}-link-${r.id}`}>{c[1](r)}</Link> : c[1](r)}</td>
        ))}</tr>
      ))}</tbody>
    </table></div>
  )
);

export const ApprovalList = ({ items }) => (
  <div className="space-y-3" data-testid="approval-history">
    {!items?.length && <div className="text-sm text-muted-foreground">No approval requests yet.</div>}
    {(items || []).map((a) => (
      <div key={a.id} className="border rounded-md p-3 text-sm">
        <div className="flex flex-wrap items-center gap-2"><span className="font-mono-g">{a.number}</span><St s={a.status} /><span className="text-muted-foreground">{a.doc_type.replace(/_/g, " ")} · {money(a.amount)} · {a.policy_name}</span></div>
        <div className="text-[11px] text-muted-foreground mt-1">Requested by {a.requester_name} · {fmtDateTime(a.submitted_at)} · stage {a.stage_index + 1}/{a.stages.length}</div>
        {a.decisions.map((d, i) => <div key={i} className="text-xs mt-1">• {d.name} ({d.role}) {d.decision.replace("_", " ")} {d.on_behalf_of ? "(delegated)" : ""} · {fmtDateTime(d.at)} {d.comment ? `— ${d.comment}` : ""}</div>)}
      </div>
    ))}
  </div>
);

export function useAction(onDone) {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const run = async (method, url, body, msg) => {
    setBusy(true);
    try { const r = await api[method](url, body); if (msg) toast.success(msg); onDone?.(r.data); return r.data; } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); }
  };
  return { run, busy };
}

export const FormDialog = ({ open, onOpenChange, title, description, fields, onSubmit, testId }) => {
  const [v, setV] = useState({});
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setV({}); }}>
      <DialogContent data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <div className="space-y-3">{fields.map((f) => (
          <Field key={f.name} label={f.label}>
            {f.render ? f.render(v[f.name], (x) => setV((p) => ({ ...p, [f.name]: x }))) : f.textarea
              ? <Textarea value={v[f.name] || ""} onChange={(e) => setV((p) => ({ ...p, [f.name]: e.target.value }))} data-testid={`${testId}-${f.name}`} />
              : <Input type={f.type || "text"} value={v[f.name] ?? ""} onChange={(e) => setV((p) => ({ ...p, [f.name]: e.target.value }))} data-testid={`${testId}-${f.name}`} />}
          </Field>
        ))}</div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={async () => { if (await onSubmit(v)) { setV({}); onOpenChange(false); } }} data-testid={`${testId}-submit`}>Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
